import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  Menu,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { BglPrimaryButton } from 'components/button/BglButtons';
import { useGlobalConfig } from 'context/GlobalContext';
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { ArrowLeft, Key, Plus, Info, ChevronDown, X } from 'lucide-react';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { ClusterApis } from 'apis/clusterAPI';
import { useToast } from 'context/ToastContext';
import { PermissionGate } from 'components/access/PermissionGate';
import { UI_PAGES } from 'framework/access/UiRegistry';
import { 
  AccessButton, 
  AccessStatusDropdown, 
  getStatusIcon, 
  AccessStatus 
} from 'components/common/AccessStatusButton';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease-in-out',
}));

// API response item interface
interface FeatureMetricItem {
  featureId: string;
  featureName: string;
  clusterReference: string;
  resolution: AccessStatus;
  [key: string]: any;
}

// Transformed feature structure for display
interface Feature {
  id: string;
  name: string;
  clusterResolutions: { [clusterReference: string]: AccessStatus };
}

export const FeatureAccessPage: React.FC = () => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const toast = useToast();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  
  // State
  const [features, setFeatures] = useState<Feature[]>([]);
  const [clusterColumns, setClusterColumns] = useState<string[]>([]);
  const [availableClusters, setAvailableClusters] = useState<{ clusterReference: string; clusterName: string; clusterId: string }[]>([]);
  const [filter, setFilter] = useState('all');
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dropdownAnchor, setDropdownAnchor] = useState<{ [key: string]: HTMLElement | null }>({});
  const [addClusterAnchor, setAddClusterAnchor] = useState<null | HTMLElement>(null);
  const addClusterOpen = Boolean(addClusterAnchor);

  // Track page load on mount
  useEffect(() => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.FEATURE_ACCESS_LOADED)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch feature access data on mount
  useEffect(() => {
    const fetchFeatureAccess = async () => {
      if (!selectedPilotId) {
        return;
      }

      try {
        setLoading(true);
        const response = await ClusterApis.getFeatureMetric(rc, selectedPilotId.toString(), 'LEVEL_1');
        
        if (response.success && response.data) {
          // Get the raw data array
          const responseData = response.data as any;
          const rawData: FeatureMetricItem[] = responseData.features || responseData.data || responseData;
          
          if (Array.isArray(rawData)) {
            // Extract unique clusterReference values for columns
            const uniqueClusters = [...new Set(rawData.map((item: FeatureMetricItem) => 
              item.clusterReference || item.cluster_reference || ''
            ).filter(Boolean))];
            setClusterColumns(uniqueClusters);

            // Group data by featureId and build resolution map
            const featureMap = new Map<string, { 
              featureId: string;
              featureName: string;
              clusterResolutions: { [clusterReference: string]: 'access' | 'pass' | 'kill' };
            }>();
            
            rawData.forEach((item: FeatureMetricItem) => {
              const featureId = item.featureId || item.feature_id || '';
              const featureName = item.featureName || item.feature_name || item.name || '';
              const clusterRef = item.clusterReference || item.cluster_reference || '';
              const resolution = (item.resolution || 'pass').toLowerCase() as 'access' | 'pass' | 'kill';
              
              if (!featureMap.has(featureId)) {
                featureMap.set(featureId, {
                  featureId: featureId,
                  featureName: featureName,
                  clusterResolutions: {}
                });
              }
              featureMap.get(featureId)!.clusterResolutions[clusterRef] = resolution;
            });

            // Transform to Feature array
            const transformedFeatures: Feature[] = Array.from(featureMap.values()).map((featureData) => ({
              id: featureData.featureId,
              name: featureData.featureName,
              clusterResolutions: featureData.clusterResolutions,
            }));
            
            setFeatures(transformedFeatures);
          }
        } else {
          toast.error(response.error || 'Failed to fetch feature access data');
        }
      } catch (error) {
        rc.error(rc.getName(FeatureAccessPage), 'Error fetching feature access:', error);
        toast.error('Failed to fetch feature access data');
      } finally {
        setLoading(false);
      }
    };

    fetchFeatureAccess();
  }, [rc, selectedPilotId]);

  // Fetch available clusters for the Add Cluster dropdown (only Feature category clusters)
  useEffect(() => {
    const fetchClusters = async () => {
      if (!selectedPilotId) {
        return;
      }

      try {
        const response = await ClusterApis.getClustersList(rc, selectedPilotId.toString(), 'FEATURE');
        
        if (response.success && response.data) {
          const clusters = response.data.map((cluster: any) => ({
            clusterId: cluster.cluster_id || cluster.clusterId || '',
            clusterName: cluster.cluster_name || cluster.clusterName || '',
            clusterReference: cluster.cluster_reference || cluster.clusterReference || '',
          }));
          setAvailableClusters(clusters);
        }
      } catch (error) {
        rc.error(rc.getName(FeatureAccessPage), 'Error fetching clusters:', error);
      }
    };

    fetchClusters();
  }, [rc, selectedPilotId]);

  const handleBack = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS);
  };

  const handleAccessChange = (featureId: string, clusterReference: string, newStatus: 'access' | 'pass' | 'kill') => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.FEATURE_ACCESS_FEATURE_TOGGLED, {
      is_enabled: newStatus === 'access',
    });
    setFeatures(prev => prev.map(feature =>
      feature.id === featureId 
        ? { 
            ...feature, 
            clusterResolutions: { 
              ...feature.clusterResolutions, 
              [clusterReference]: newStatus 
            } 
          }
        : feature
    ));
    setHasChanges(true);
  };

  const handleDropdownOpen = (event: React.MouseEvent<HTMLElement>, featureId: string, column: string) => {
    const key = `${featureId}-${column}`;
    setDropdownAnchor(prev => ({ ...prev, [key]: event.currentTarget }));
  };

  const handleDropdownClose = (featureId: string, column: string) => {
    const key = `${featureId}-${column}`;
    setDropdownAnchor(prev => ({ ...prev, [key]: null }));
  };

  const handleSaveChanges = async () => {
    if (!selectedPilotId) {
      toast.error('No project selected');
      return;
    }

    setSaving(true);

    try {
      // Group features by component (EMAIL, DASHBOARD, HER) based on featureId prefix
      const componentGroups: { [key: string]: Feature[] } = {
        EMAIL: [],
        DASHBOARD: [],
        HER: []
      };

      features.forEach(feature => {
        const prefix = feature.id.split('.')[0].toUpperCase();
        if (prefix in componentGroups) {
          componentGroups[prefix].push(feature);
        }
      });

      // Build headers array from cluster columns
      // First column is always FEATURE_ID, followed by cluster references (keep original casing)
      const headers = ['FEATURE_ID', ...clusterColumns];

      // Build files array for each component that has features
      const files: {
        component: 'EMAIL' | 'DASHBOARD' | 'HER';
        level: 'L1' | 'L2';
        headers: string[];
        features: { featureId: string; values: string[] }[];
      }[] = [];

      (['EMAIL', 'DASHBOARD', 'HER'] as const).forEach(component => {
        const componentFeatures = componentGroups[component];
        if (componentFeatures.length > 0) {
          files.push({
            component,
            level: 'L1', // Using L1 as default based on current fetching level
            headers,
            features: componentFeatures.map(feature => ({
              featureId: feature.id,
              // Map cluster resolutions to values array in the same order as headers
              values: clusterColumns.map(clusterRef => {
                const resolution = feature.clusterResolutions[clusterRef] || 'pass';
                return resolution.toUpperCase();
              })
            }))
          });
        }
      });

      if (files.length === 0) {
        toast.error('No features to save');
        setSaving(false);
        return;
      }

      const payload = {
        projectId: selectedPilotId.toString(),
        files
      };

      const response = await ClusterApis.saveFeatureAccess(rc, payload);

      if (response.success && response.data) {
        const responseData = response.data;
        
        // Check if all files were uploaded and API was notified
        const uploadedFiles = responseData.uploaded_files || [];
        const allNotified = uploadedFiles.every(file => file.api_notified);
        
        if (allNotified) {
          toast.success(responseData.message || 'Feature access saved successfully');
        } else {
          // Some files may have notification errors
          const failedFiles = uploadedFiles.filter(file => !file.api_notified);
          const failedNames = failedFiles.map(f => f.file_name).join(', ');
          toast.warning(`Feature access saved, but some files failed to notify backend: ${failedNames}`);
        }

        rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.FEATURE_ACCESS_SAVED, {
          feature_count: features.length,
          cluster_count: clusterColumns.length,
        });

        // Show any additional errors
        if (responseData.errors && responseData.errors.length > 0) {
          responseData.errors.forEach((error: any) => {
            const errorMessage = typeof error === 'object' ? (error.message || JSON.stringify(error)) : error;
            toast.error(errorMessage);
          });
        }

        setHasChanges(false);
      } else {
        // Handle error object with code/message structure
        const errorMessage = typeof response.error === 'object' 
          ? (response.error?.message || JSON.stringify(response.error)) 
          : (response.error || 'Failed to save feature access');
        toast.error(errorMessage);
      }
    } catch (error) {
      rc.error(rc.getName(FeatureAccessPage), 'Error saving feature access:', error);
      toast.error('Failed to save feature access');
    } finally {
      setSaving(false);
    }
  };

  const handleAddClusterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAddClusterAnchor(event.currentTarget);
  };

  const handleAddClusterClose = () => {
    setAddClusterAnchor(null);
  };

  const handleClusterTypeSelect = (clusterReference: string) => {
    // Check if cluster is already added
    if (clusterColumns.includes(clusterReference)) {
      handleAddClusterClose();
      return;
    }

    // Add the cluster as a new column
    setClusterColumns(prev => [...prev, clusterReference]);

    // Set default 'pass' resolution for all features in this new cluster
    setFeatures(prev => prev.map(feature => ({
      ...feature,
      clusterResolutions: {
        ...feature.clusterResolutions,
        [clusterReference]: 'pass'
      }
    })));

    setHasChanges(true);
    handleAddClusterClose();
  };

  const handleRemoveCluster = (clusterReference: string) => {
    // Remove the cluster column
    setClusterColumns(prev => prev.filter(col => col !== clusterReference));

    // Remove the cluster resolution from all features
    setFeatures(prev => prev.map(feature => {
      const { [clusterReference]: removed, ...remainingResolutions } = feature.clusterResolutions;
      return {
        ...feature,
        clusterResolutions: remainingResolutions
      };
    }));

    setHasChanges(true);
  };

  // Filter out clusters that are already added as columns
  const availableClusterOptions = availableClusters.filter(
    cluster => !clusterColumns.includes(cluster.clusterReference)
  );

  const filteredFeatures = filter === 'all' 
    ? features 
    : features.filter(feature => feature.id.toLowerCase().includes(filter.toLowerCase()));

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: 'background.default',
      p: 3,
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Feature Access
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={16} />}
            onClick={handleBack}
            sx={{ color: 'text.secondary', borderColor: 'divider' }}
          >
            Back to Cluster Configuration
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ mx: 'auto' }}>
        <StyledCard>
          <CardContent sx={{ p: 4,textAlign:'left' }}>
            {/* Feature Access Matrix Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: .5 }}>
                    <Key size={24} style={{ marginRight: 12, color: theme.palette.primary.main }} />
                    <Typography variant="h5" sx={{ fontWeight: 600}}>
                        Feature Access Matrix
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Manage feature accessibility based on 'Feature' category clusters. This list is derived from the Application Scope page; only enabled applications are displayed here.
                </Typography>
              </Box>
              <Box sx={{ ml: 'auto' }}>
                <Button
                  variant="outlined"
                  startIcon={<Plus size={16} />}
                  endIcon={<ChevronDown size={16} />}
                  onClick={handleAddClusterClick}
                  aria-controls={addClusterOpen ? 'add-cluster-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={addClusterOpen ? 'true' : undefined}
                  sx={{ color: 'text.secondary', borderColor: 'divider' }}
                >
                  Add Cluster
                </Button>
                <Menu
                  id="add-cluster-menu"
                  anchorEl={addClusterAnchor}
                  open={addClusterOpen}
                  onClose={handleAddClusterClose}
                  MenuListProps={{
                    'aria-labelledby': 'add-cluster-button',
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    sx: {
                      borderRadius: 2,
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                      border: '1px solid #E0E0E0',
                      minWidth: '160px',
                      '& .MuiMenuItem-root': {
                        padding: '12px 16px',
                        fontSize: '14px',
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                      },
                    },
                  }}
                >
                  {availableClusterOptions.length > 0 ? (
                    availableClusterOptions.map((cluster) => (
                      <MenuItem 
                        key={cluster.clusterId || cluster.clusterReference} 
                        onClick={() => handleClusterTypeSelect(cluster.clusterReference)}
                      >
                        {cluster.clusterReference || cluster.clusterName}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      {availableClusters.length === 0 ? 'Loading clusters...' : 'All clusters added'}
                    </MenuItem>
                  )}
                </Menu>
              </Box>
            </Box>

            {/* Filter */}
            <Box sx={{ mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 300 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary',marginBottom:.5 }}>Filter by Feature</Typography>
                <Select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                //   label=""
                >
                  <MenuItem value="all">All Features</MenuItem>
                  {features.map((feature) => (
                    <MenuItem key={feature.id} value={feature.id}>{feature.id}</MenuItem>
                  ))}
                  {/* <MenuItem value="email">Email Features</MenuItem>
                  <MenuItem value="dashboard">Dashboard Features</MenuItem> */}
                </Select>
              </FormControl>
            </Box>

            {/* Feature Access Table */}
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                py: 8,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                bgcolor: 'background.default'
              }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                  Loading feature access data...
                </Typography>
              </Box>
            ) : features.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 8, 
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                bgcolor: 'background.default'
              }}>
                <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                  No features found
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No feature access data available for this pilot.
                </Typography>
              </Box>
            ) : (
            <TableContainer component={Paper} sx={{ 
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
            }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'background.default' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Feature
                        <Tooltip title="Feature access configuration">
                          <Info size={16} style={{ marginLeft: 4, color: theme.palette.text.secondary }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    {/* Dynamic cluster columns */}
                    {clusterColumns.map((clusterRef, index) => {
                      const isDefaultCluster = index === 0;
                      return (
                        <TableCell 
                          key={clusterRef} 
                          align="center" 
                          sx={{ 
                            fontWeight: 600,
                            '&:hover .remove-column-btn': {
                              opacity: 1,
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            {clusterRef.toUpperCase()}
                            {!isDefaultCluster && (
                              <Tooltip title="Remove column">
                                <IconButton
                                  className="remove-column-btn"
                                  size="small"
                                  onClick={() => handleRemoveCluster(clusterRef)}
                                  sx={{
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    p: 0.5,
                                    bgcolor: 'primary.main',
                                    color: 'error.main',
                                    '&:hover': {
                                      bgcolor: 'primary.dark',
                                    },
                                  }}
                                >
                                  <X size={14} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFeatures.map((feature) => (
                    <TableRow key={feature.id} hover>
                      <TableCell sx={{px:1,py:0.5}}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize:'12px' }}>
                          {feature.id}
                        </Typography>
                      </TableCell>
                      {/* Dynamic cluster cells */}
                      {clusterColumns.map((clusterRef) => {
                        const resolution = feature.clusterResolutions[clusterRef] || 'pass';
                        return (
                          <TableCell key={clusterRef} align="center" sx={{px:1,py:0.5, textAlign: 'center'}}>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <AccessButton
                                status={resolution}
                                onClick={(e) => handleDropdownOpen(e, feature.id, clusterRef)}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
                                  {getStatusIcon(resolution)}
                                  {resolution.toUpperCase()}
                                </Box>
                                <ChevronDown size={14} />
                              </AccessButton>
                            </Box>
                            <AccessStatusDropdown
                              status={resolution}
                              anchorEl={dropdownAnchor[`${feature.id}-${clusterRef}`]}
                              onClose={() => handleDropdownClose(feature.id, clusterRef)}
                              onSelect={(newStatus) => handleAccessChange(feature.id, clusterRef, newStatus)}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            )}

            {/* Save Changes Button */}
            {hasChanges && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <PermissionGate require={UI_PAGES.FEATURE_ACCESS.ctas.feature_resolution.id} mode="disable">
                  <BglPrimaryButton
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleSaveChanges}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </BglPrimaryButton>
                </PermissionGate>
              </Box>
            )}
          </CardContent>
        </StyledCard>
      </Box>
    </Box>
  );
};
